import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadFinancialPaymentMethodTypeDetails,
    loadFinancialRequestStatusDetails,
    loadFinancialRequestTypeDetails
} from '../../../../store/directusService';
import { loadFinancialRequests } from '../../../../store/participantService';
import { selectUsersForNames } from '../../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../../store/userService';
import {
    DEFAULT_TABLE_ROWS_PER_PAGE,
    EDIT_ON_CLOSED_ROLES
} from '../../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import CreateFinancialRequest from './CreateFinancialRequest';
import EditFinancialRequest from './EditFinancialRequest';
import FinancialRequestRow from './FinancialRequestRow';
import GenerateFinancialRequestPDF from './GenerateFinancialRequestPDF';

import app from '../../../../app.module.css';
import pdf from '../../../../commonStyles/pdf.module.css';
import classes from './financialRequest.module.css';

const headCells = [
    { id: 'code', numeric: false, label: 'ID' },
    { id: 'type', numeric: false, label: 'Type' },
    { id: 'amount', numeric: false, label: 'Amount' },
    { id: 'status', numeric: true, label: 'Status' },
    { id: 'statusChangeDate', numeric: true, label: 'Status Change Date' },
    { id: 'dateIssued', numeric: true, label: 'Date Issued' },
    { id: 'user', numeric: true, label: 'Authoriser' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'statusChangeDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const FinancialRequest = ({ isViewOnly = false }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [downloadRow, setDownloadRow] = useState(null);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [isDisabled, setIsDisabled] = useState(false);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantFinancialRequests = useSelector(
        (state) => state.entities.participantService.participantFinancialRequests
    );
    const financialRequestStatusDetails = useSelector(
        (state) => state.entities.directusService.financialRequestStatusDetails
    );
    const financialRequestTypeDetails = useSelector(
        (state) => state.entities.directusService.financialRequestTypeDetails
    );
    const paymentMethodDetails = useSelector(
        (state) => state.entities.directusService.financialPaymentMethodDetails
    );
    const users = useSelector(selectUsersForNames);

    // USE EFFECTS
    useEffect(() => {
        !financialRequestStatusDetails?.length && dispatch(loadFinancialRequestStatusDetails());
        !financialRequestTypeDetails?.length && dispatch(loadFinancialRequestTypeDetails());
        !paymentMethodDetails?.length && dispatch(loadFinancialPaymentMethodTypeDetails());
    }, []);

    useEffect(() => {
        if (!currentParticipant?.id) return;
        !participantFinancialRequests?.length &&
            dispatch(loadFinancialRequests(currentParticipant.id));
        const disableOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus || isViewOnly);
    }, [currentParticipant]);

    useEffect(() => {
        if (
            !participantFinancialRequests.length ||
            !financialRequestStatusDetails?.length ||
            !financialRequestTypeDetails?.length
        )
            return;
        const userIds = [
            ...new Set([
                ...participantFinancialRequests.map((el) => el.approverId),
                ...participantFinancialRequests.map((el) => el.adviserId)
            ])
        ];
        const missingUserIds = userIds
            .filter((el) => !users.find((entry) => entry.id === el))
            .filter((el) => el);

        if (missingUserIds.length > 0) dispatch(loadUsersMinimalDetails(missingUserIds));
        else
            setRows(
                participantFinancialRequests.map((el) => {
                    const authoriser = users.find((entry) => entry.id === el.approverId);
                    return {
                        ...el,
                        type:
                            financialRequestTypeDetails.find(
                                (entry) => entry.id === el.requestTypeId
                            )?.name || '',
                        status:
                            financialRequestStatusDetails.find((entry) => entry.id === el.statusId)
                                ?.name || '',
                        user: authoriser
                            ? `${authoriser?.firstName || ''} ${authoriser?.lastName || ''}`
                            : ''
                    };
                })
            );
    }, [
        financialRequestStatusDetails,
        financialRequestTypeDetails,
        participantFinancialRequests,
        users
    ]);

    // FNs
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <FinancialRequestRow
                key={el.id}
                row={el}
                onEdit={onEdit}
                onDownload={onDownload}
                isDisabled={isDisabled || showForm}
            />
        ));

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onDownload = (row) => {
        setDownloadRow(row);
    };

    const onPDFDownloaded = () => {
        setDownloadRow(null);
    };

    return (
        <div className={classes.financialRequests} data-testid="financial_requests">
            {downloadRow && (
                <div id="hiddenPDFLayer" className={pdf.previewPdfHiddenWrapper}>
                    <GenerateFinancialRequestPDF
                        financialRequest={downloadRow}
                        financialRequestDownloaded={onPDFDownloaded}
                    />
                </div>
            )}
            <h2 className={app.sectionHeading}>Financial Request Details</h2>
            <div className={classes.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content="No financial requests found" />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm && (
                    <Button
                        id="addFinancialRequestButton"
                        type="button"
                        disabled={isDisabled}
                        content="Add Financial Request"
                        icon={true}
                        onClick={onCreate}
                    />
                )}
                {showForm && formType === 'create' && (
                    <CreateFinancialRequest roles={roles} onClose={() => setShowForm(false)} />
                )}
                {showForm && formType === 'edit' && (
                    <EditFinancialRequest
                        row={selectedRow}
                        roles={roles}
                        onClose={() => setShowForm(false)}
                    />
                )}
            </div>
        </div>
    );
};

FinancialRequest.propTypes = {
    isViewOnly: PropTypes.bool
};

export default FinancialRequest;
